// src/components/ChangePasswordPage.js
//
// In-app password change for an already-signed-in user. Reached two ways:
//   1. FORCED — the login flow found the password is >90 days old and routed
//      here (location.state.forced === true) before letting the user into the
//      app. On success we continue to where they were headed.
//   2. Proactive — a user changes their password from a menu; on success we
//      just confirm.
//
// Re-authenticates with the current password (required by Firebase for a
// sensitive op), updates the password, then stamps the rotation clock.

import { useState } from "react";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { auth } from "../firebase";
import { Lock, Loader2, ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";

const RECORD_PWD_CHANGE_URL =
  `${FUNCTIONS_BASE}/recordPasswordChange`;

// Keep in sync with SetPasswordPage.
const passwordProblem = (pwd) => {
  if (pwd.length < 10) return "Use at least 10 characters.";
  if (!/[a-z]/.test(pwd) || !/[A-Z]/.test(pwd))
    return "Include both upper- and lower-case letters.";
  if (!/[0-9]/.test(pwd)) return "Include at least one number.";
  return null;
};

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const forced = location.state?.forced === true;
  const continueTo = location.state?.from || "/login";

  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const user = auth.currentUser;
    if (!user || !user.email) {
      setError("Your session expired. Please sign in again.");
      return;
    }
    const problem = passwordProblem(pwd);
    if (problem) return setError(problem);
    if (pwd !== confirm) return setError("Passwords don't match.");
    if (pwd === current)
      return setError("Choose a password different from your current one.");

    setBusy(true);
    try {
      // Re-auth is required by Firebase before updatePassword.
      const credential = EmailAuthProvider.credential(user.email, current);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, pwd);

      // Stamp the rotation clock (server-trusted).
      try {
        const idToken = await user.getIdToken(true);
        await fetch(RECORD_PWD_CHANGE_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({}),
        }).catch(() => {});
      } catch (_) {
        /* best-effort */
      }

      if (forced) {
        navigate(continueTo, { replace: true });
      } else {
        setDone(true);
      }
    } catch (err) {
      console.error(err);
      setError(
        err?.code === "auth/wrong-password" ||
          err?.code === "auth/invalid-credential"
          ? "Your current password is incorrect."
          : "Couldn't change your password. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope] flex items-center justify-center px-4">
      <div className="fixed inset-0 -z-10 overflow-hidden bg-gradient-to-br from-[#070418] via-[#0c0726] to-[#04030f]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.28),_transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(37,99,235,0.22),_transparent_55%)]" />
      </div>

      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-5">
          <ShieldCheck className="w-5 h-5 text-purple-300" />
          <h1 className="text-lg font-bold tracking-tight text-white">
            {forced ? "Password Expired — Update Required" : "Change Password"}
          </h1>
        </div>

        <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_20px_60px_-20px_rgba(124,58,237,0.5)] p-5">
          {forced && !done && (
            <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
              Your password is over 90 days old. Set a new one to continue.
            </div>
          )}

          {error && (
            <div className="mb-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {done ? (
            <div className="text-center py-4 space-y-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-300 mx-auto" />
              <p className="text-sm text-slate-200">Your password has been updated.</p>
              <Link
                to="/login"
                className="inline-block text-sm text-purple-300 hover:text-purple-200 underline"
              >
                Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3.5">
              {[
                ["Current password", current, setCurrent],
                ["New password", pwd, setPwd],
                ["Confirm new password", confirm, setConfirm],
              ].map(([label, value, setter]) => (
                <div key={label}>
                  <label className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                    {label}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="password"
                      className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50"
                      value={value}
                      onChange={(e) => setter(e.target.value)}
                      required
                    />
                  </div>
                </div>
              ))}

              <p className="text-[11px] text-slate-500">
                At least 10 characters, with upper- and lower-case letters and a
                number.
              </p>

              <button
                type="submit"
                disabled={busy}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white text-sm font-semibold ring-1 ring-white/20 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Updating…
                  </>
                ) : (
                  "Update password"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
